/* The first screen. Strava's export is the main source; a watch export is optional and adds to it. */
import type { Dataset } from '../import/types';
import type { ImportMessage, ImportRequest } from '../import/worker';
import { CHECKED, WATCHES } from './sources';
import { isReportFileName, readReportFile } from './report-file';
import { feedbackLine } from './feedback';
import { store } from './store';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

export interface ImportHooks {
  onData: (d: Dataset, remember: boolean, note?: string) => void;
  onSample: () => void;
  saved: Dataset | null; // a poster saved on this device earlier
  onOpenSaved: () => void;
  onDeleteSaved: () => void;
}

export function showImport({ onData, onSample, saved, onOpenSaved, onDeleteSaved }: ImportHooks) {
  const savedOn = saved ? new Date(saved.built).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  $('srcTag').textContent = '';
  $('foot').innerHTML = '<span>Free. Your exports are read in this browser and never uploaded. Not medical or coaching advice.</span>' + feedbackLine();
  // One screen: the drop zone first; the steps for each export open on tap, one at a time.
  const stravaSteps = [
    'On strava.com (not the app), set your language to English in Settings.',
    'Settings → My Account → Download or Delete Your Account → Get Started → Request Your Archive.',
    'Strava emails a link, usually within a few hours. Download the ZIP and drop it here.',
  ];
  const numbered = (xs: string[]) => `<ol class="nsteps">${xs.map((x, i) => `<li><b>${i + 1}</b><span>${x}</span></li>`).join('')}</ol>`;
  $('app').innerHTML = `
  <section class="panel night imp" id="start"><div class="in">
    <h1 class="headline hero-s imp-center">Every run. <em>One poster.</em></h1>
    ${saved ? `<div class="saved-bar"><span>Your poster from <b>${savedOn}</b> is saved on this device (${saved.runs.length.toLocaleString()} runs).</span><span class="saved-btns"><button type="button" class="go small" id="openSaved">Open it</button><button type="button" class="ghost" id="delSaved">Delete it</button></span></div>` : ''}
    <div class="drop" id="drop" tabindex="0" role="button" aria-describedby="dropHint">
      <input type="file" id="pick" accept=".zip,.fit,.gz,.json,.txt,application/zip,application/json,text/plain" multiple hidden>
      <span class="drop-big">Drop your Strava export here</span>
      <span class="drop-hint" id="dropHint">The ZIP Strava emails you · <b>plus your watch’s export</b> for a fuller report (optional) · or <u>choose files</u></span>
      <span class="drop-hint">Don’t have your export yet? <button type="button" class="linkish" id="seeHow">See how ↓</button></span>
    </div>
    <ul class="files" id="files" aria-live="polite"></ul>
    <div class="help-cards">
      <button type="button" class="help-card" data-drawer="strava" aria-haspopup="dialog"><span class="hc-k">Needed</span><span class="hc-q">How do I get my Strava export?</span><span class="hc-go">See the steps <span class="hc-arrow" aria-hidden="true">→</span></span></button>
      <button type="button" class="help-card" data-drawer="watch" aria-haspopup="dialog"><span class="hc-k">Optional</span><span class="hc-q">How do I get my watch’s export for a fuller report?</span><span class="hc-go">See the steps <span class="hc-arrow" aria-hidden="true">→</span></span></button>
    </div>
    <div class="imp-opts">
      <label class="opt"><input type="checkbox" id="wx"> <span>Add the weather <span class="dim">· shows how heat changes your effort; sends rough start areas (about 25 km) and dates to Open-Meteo</span></span></label>
      <label class="opt"><input type="checkbox" id="keep" ${saved ? 'checked' : ''}> <span>Remember on this device <span class="dim">· so you don’t have to import again</span></span></label>
    </div>
    <div class="go-row imp-center-row">
      <button type="button" class="go" id="go" disabled>Make my poster</button>
      <button type="button" class="ghost" id="sample">Try a sample runner</button>
    </div>
    <div class="prog" id="prog" hidden><div class="prog-bar"><span id="progFill"></span></div><span class="prog-t" id="progT" role="status"></span></div>
    <p class="err" id="err" role="alert" hidden></p>
    <p class="imp-note">On your phone? Choose the report file you sent from your computer.</p>
    <p class="imp-note privacy"><b>0</b> uploads · <b>0</b> accounts · <b>1</b> optional lookup (weather) · your exports are read in this browser</p>
  </div></section>
  <div class="drawer-bg" id="drawerBg" hidden></div>
  <aside class="drawer panel cobalt" id="drawer" role="dialog" aria-modal="true" aria-labelledby="drawerTitle" hidden><div class="in">
    <button type="button" class="drawer-x" id="drawerX" aria-label="Close">×</button>
    <div data-pane="strava">
      <h2 class="sub-h" id="drawerTitle">Your Strava export <span class="chip need">Needed</span></h2>
      <p class="what">Your runs, titles and races: the backbone of the poster. A free account works.</p>
      ${numbered(stravaSteps)}
    </div>
    <div data-pane="watch" hidden>
      <h2 class="sub-h">Your watch’s export <span class="chip opt">Optional</span></h2>
      <p class="what">Adds what Strava doesn’t have, for a more rounded report.</p>
      <label class="watch-pick"><span>Which watch?</span>
        <select id="watch">${WATCHES.map((w) => `<option value="${w.id}">${w.name}</option>`).join('')}</select></label>
      <div id="watchInfo" class="watch-info" aria-live="polite"></div>
    </div>
    <p class="checked">Steps checked ${CHECKED}.</p>
  </div></aside>`;

  // ---------- watch picker ----------
  const watchSel = $<HTMLSelectElement>('watch');
  const showWatch = () => {
    const w = WATCHES.find((x) => x.id === watchSel.value) || WATCHES[0];
    store.set('watch', w.id);
    $('watchInfo').innerHTML = w.supported === 'no'
      ? `<p class="soon">Not supported yet</p>${w.steps.map((x) => `<p class="plain">${x}</p>`).join('')}`
      : `<ul class="adds">${w.adds.map((a) => `<li>${a}</li>`).join('')}</ul>${numbered(w.steps)}${w.note ? `<p class="note">${w.note}</p>` : ''}`;
  };
  watchSel.value = WATCHES.some((w) => w.id === store.get('watch', 'garmin')) ? store.get('watch', 'garmin') : 'garmin';
  watchSel.onchange = showWatch;
  showWatch();

  // ---------- the steps open in a side panel, one export at a time ----------
  const drawer = $('drawer'), drawerBg = $('drawerBg');
  let opener: HTMLElement | null = null;
  const closeDrawer = () => { drawer.hidden = true; drawerBg.hidden = true; opener?.focus(); };
  const openDrawer = (which: string, from: HTMLElement) => {
    opener = from;
    drawer.querySelectorAll<HTMLElement>('[data-pane]').forEach((p) => (p.hidden = p.dataset.pane !== which));
    drawer.setAttribute('aria-labelledby', which === 'strava' ? 'drawerTitle' : '');
    drawer.hidden = false; drawerBg.hidden = false;
    $('drawerX').focus();
  };
  document.querySelectorAll<HTMLButtonElement>('[data-drawer]').forEach((b) => (b.onclick = () => openDrawer(b.dataset.drawer!, b)));
  $('seeHow').onclick = (e) => { e.stopPropagation(); openDrawer('strava', e.currentTarget as HTMLElement); };
  $('drawerX').onclick = closeDrawer;
  drawerBg.onclick = closeDrawer;
  drawer.onkeydown = (e) => { if (e.key === 'Escape') closeDrawer(); };

  const drop = $('drop'), pick = $<HTMLInputElement>('pick'), list = $('files'), go = $<HTMLButtonElement>('go');
  let files: File[] = [];
  const mb = (n: number) => `${(n / 1048576).toFixed(n < 10485760 ? 1 : 0)} MB`;
  const showFiles = () => {
    list.innerHTML = files.map((f, i) => `<li><span class="fname">${f.name.replace(/[<>&"]/g, '')}</span><span class="fsize">${mb(f.size)}</span><button type="button" class="x" data-i="${i}" aria-label="Remove ${f.name.replace(/[<>&"]/g, '')}">×</button></li>`).join('');
    list.querySelectorAll<HTMLButtonElement>('.x').forEach((b) => (b.onclick = () => { files.splice(Number(b.dataset.i), 1); showFiles(); }));
    go.disabled = !files.length;
  };
  const add = (fl: FileList | null) => {
    if (!fl) return;
    for (const f of Array.from(fl)) if (!files.some((x) => x.name === f.name && x.size === f.size)) files.push(f);
    $('err').hidden = true; showFiles();
  };
  drop.onclick = () => pick.click();
  drop.onkeydown = (e) => { if (e.target !== drop) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick.click(); } };
  pick.onchange = () => { add(pick.files); pick.value = ''; };
  drop.ondragover = (e) => { e.preventDefault(); drop.classList.add('over'); };
  drop.ondragleave = () => drop.classList.remove('over');
  drop.ondrop = (e) => { e.preventDefault(); drop.classList.remove('over'); add(e.dataTransfer?.files ?? null); };
  $('sample').onclick = onSample;
  if (saved) { $('openSaved').onclick = onOpenSaved; $('delSaved').onclick = onDeleteSaved; }

  go.onclick = async () => {
    const prog = $('prog'), fill = $('progFill'), txt = $('progT'), err = $('err');
    // a report file sent from another device opens straight away
    const rf = files.find((f) => isReportFileName(f.name));
    if (rf) {
      try {
        const { dataset, notRace, name } = await readReportFile(rf);
        store.set('notRace.me', JSON.stringify(notRace));
        // the name as it showed on the sending device (edited or hidden); older files carry none
        if (name !== null) store.set('name.me', name);
        onData(dataset, $<HTMLInputElement>('keep').checked);
      } catch (e) { err.textContent = (e as Error).message; err.hidden = false; }
      return;
    }
    go.disabled = true; prog.hidden = false; err.hidden = true;
    const worker = new Worker(new URL('../import/worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (ev: MessageEvent<ImportMessage>) => {
      const m = ev.data;
      if (m.kind === 'progress') {
        txt.textContent = m.total ? `${m.stage} · ${m.done} of ${m.total}` : m.stage;
        fill.style.width = m.total ? `${Math.round(((m.done ?? 0) / m.total) * 100)}%` : '8%';
      } else if (m.kind === 'done') {
        worker.terminate();
        onData(m.dataset, $<HTMLInputElement>('keep').checked, m.weatherError ? `The weather lookup didn't work (${m.weatherError}), so the report has no weather.` : undefined);
      } else {
        worker.terminate();
        prog.hidden = true; go.disabled = false;
        err.textContent = m.message; err.hidden = false;
      }
    };
    worker.onerror = (e) => { worker.terminate(); prog.hidden = true; go.disabled = false; err.textContent = `Something went wrong reading the files (${e.message}).`; err.hidden = false; };
    const req: ImportRequest = { files, weather: $<HTMLInputElement>('wx').checked, offsetMin: -new Date().getTimezoneOffset() };
    worker.postMessage(req);
  };
  window.scrollTo(0, 0);
}
